import { Box, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import type { TimeLeft } from '../types';

interface CountdownTimerProps {
    timeLeft: TimeLeft;
}

/**
 * Componente de contador regressivo para exibição de tempo restante
 * Exibe dias, horas e minutos em cards brancos com números azuis
 * Inclui animação quando os valores mudam
 */
export function CountdownTimer({ timeLeft }: CountdownTimerProps) {
    const [animating, setAnimating] = useState({
        days: false,
        hours: false,
        minutes: false,
    });

    const prevValuesRef = useRef({ days: timeLeft.days, hours: timeLeft.hours, minutes: timeLeft.minutes });

    useEffect(() => {
        // Detecta mudanças individuais e anima apenas o que mudou
        const changes = {
            days: prevValuesRef.current.days !== timeLeft.days,
            hours: prevValuesRef.current.hours !== timeLeft.hours,
            minutes: prevValuesRef.current.minutes !== timeLeft.minutes,
        };

        if (changes.days || changes.hours || changes.minutes) {
            setAnimating(changes);

            // Remove a animação após completar
            const timer = setTimeout(() => {
                setAnimating({ days: false, hours: false, minutes: false });
            }, 600);

            prevValuesRef.current = {
                days: timeLeft.days,
                hours: timeLeft.hours,
                minutes: timeLeft.minutes,
            };

            return () => clearTimeout(timer);
        }
    }, [timeLeft]);

    const timeUnits = [
        { value: timeLeft.days, label: 'Dias', key: 'days' },
        { value: timeLeft.hours, label: 'Horas', key: 'hours' },
        { value: timeLeft.minutes, label: 'Minutos', key: 'minutes' },
    ];

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: { xs: 0.5, sm: 0.75, md: 1.25 },
                flexWrap: 'nowrap',
                justifyContent: 'center',
            }}
        >
            {timeUnits.map((unit) => {
                const isAnimating = animating[unit.key as keyof typeof animating];

                return (
                    <Box
                        key={unit.key}
                        sx={{
                            textAlign: 'center',
                            bgcolor: 'white',
                            px: { xs: 1, sm: 1.25, md: 2 },
                            py: { xs: 0.5, sm: 0.75, md: 1.25 },
                            borderRadius: 1,
                            minWidth: { xs: 48, sm: 55, md: 70 },
                            maxWidth: { xs: 55, sm: 60, md: 70 },
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        <Typography
                            key={`${unit.key}-${unit.value}`}
                            variant="h6"
                            fontWeight={700}
                            sx={{
                                color: '#1976D2',
                                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
                                mb: { xs: 0.25, sm: 0.5 },
                                lineHeight: 1.2,
                                fontFamily: 'Manrope, sans-serif',
                                position: 'relative',
                                transform: isAnimating ? 'scale(1.1)' : 'scale(1)',
                                transition: 'transform 0.3s ease-in-out',
                                animation: isAnimating ? 'flipNumber 0.6s ease-in-out' : 'none',
                                '@keyframes flipNumber': {
                                    '0%': {
                                        transform: 'rotateY(0deg) scale(1)',
                                        opacity: 1,
                                    },
                                    '50%': {
                                        transform: 'rotateY(90deg) scale(0.8)',
                                        opacity: 0.5,
                                    },
                                    '100%': {
                                        transform: 'rotateY(0deg) scale(1)',
                                        opacity: 1,
                                    },
                                },
                            }}
                        >
                            {String(unit.value).padStart(2, '0')}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#1976D2',
                                fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                                fontWeight: 500,
                                fontFamily: 'Manrope, sans-serif',
                                lineHeight: 1.2,
                            }}
                        >
                            {unit.label}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

